import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});

/**
 * A bot oldala (bot.mortisengine.com) élő előfizetést kér — a szerver minden kérésnél megnézi (bot-backend:
 * middleware/session.js). Ha belépve lejár, vagy a token-frissítést utasítja el, 403 SUBSCRIPTION_REQUIRED jön:
 * kiléptetünk, és a belépő lap kiírja az okát (szövegként, link nélkül — a fiók kezelése a Codex oldalon van).
 */
export const NOTICE_KEY = 'botLoginNotice';

function endSession(message) {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  if (message) {
    try { sessionStorage.setItem(NOTICE_KEY, message); } catch { /* tiltott tároló: a belépő lap üzenet nélkül jön */ }
  }
  if (window.location.pathname !== '/login') window.location.href = '/login';
}

const subscriptionEnded = (err) =>
  err?.response?.status === 403 && err.response.data?.code === 'SUBSCRIPTION_REQUIRED';

// Request interceptor - mindig a legfrissebb token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor - automatikus token frissítés 401-nél
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    // Belépve lejárt az előfizetés (a belépés saját hibáját a belépő lap mutatja).
    if (subscriptionEnded(error) && original?.url !== '/auth/login') {
      endSession(error.response.data.error);
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      error.response?.data?.code === 'TOKEN_EXPIRED' &&
      !original._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        processQueue(error);
        isRefreshing = false;
        endSession(null);
        return Promise.reject(error);
      }

      try {
        const res = await axios.post('/api/auth/refresh', { refreshToken });
        const { accessToken, refreshToken: newRefresh } = res.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefresh);
        api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        processQueue(null, accessToken);
        original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch (refreshError) {
        processQueue(refreshError);
        endSession(subscriptionEnded(refreshError) ? refreshError.response.data.error : null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
